import { useEffect, useRef, useState } from "react";
import { Camera, SwitchCamera, Wifi, WifiOff, Zap } from "lucide-react";
import { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const FPS = [1, 2, 4, 8];
const SIZES = [[640, "640p"], [960, "960p"], [1280, "720p HD"]];

export default function PhoneCam() {
  const { t } = useApp();
  const token = new URLSearchParams(window.location.search).get("t") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [running, setRunning] = useState(false);
  const [fps, setFps] = useState(2);
  const [size, setSize] = useState(960);
  const [facing, setFacing] = useState("environment");
  const [sent, setSent] = useState(0);
  const [online, setOnline] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const lockRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/cameras/phone/${token}`).then(async (r) => { if (!r.ok) throw new Error(); setInfo(await r.json()); }).catch(() => setErr(t("cam_phone_invalid")));
  }, [token, t]);

  const start = async () => {
    try {
      streamRef.current?.getTracks().forEach((x) => x.stop());
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      streamRef.current = s; videoRef.current.srcObject = s; await videoRef.current.play();
      lockRef.current = await navigator.wakeLock?.request?.("screen").catch(() => null);
      setRunning(true); setErr("");
    } catch (e) { setErr(t("cam_phone_denied")); }
  };
  const stop = () => { streamRef.current?.getTracks().forEach((x) => x.stop()); lockRef.current?.release?.(); setRunning(false); };
  useEffect(() => { if (running) start(); /* eslint-disable-next-line */ }, [facing]);

  useEffect(() => {
    if (!running) return undefined;
    const canvas = document.createElement("canvas");
    let busy = false;
    const id = setInterval(async () => {
      const v = videoRef.current;
      if (busy || !v || v.videoWidth === 0) return;
      busy = true;
      const scale = Math.min(1, size / v.videoWidth);
      canvas.width = Math.round(v.videoWidth * scale); canvas.height = Math.round(v.videoHeight * scale);
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(async (blob) => {
        try { const r = await fetch(`${API}/cameras/phone/${token}/frame`, { method: "POST", body: blob, headers: { "Content-Type": "image/jpeg" } }); setOnline(r.ok); if (r.ok) setSent((n) => n + 1); } catch { setOnline(false); } finally { busy = false; }
      }, "image/jpeg", 0.6);
    }, 1000 / fps);
    return () => clearInterval(id);
  }, [running, fps, size, token]);

  return (
    <div className="min-h-screen bg-[#0B1220] text-white flex flex-col" data-testid="phone-cam-page">
      <header className="p-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#C7353B] grid place-items-center"><Camera className="w-5 h-5" /></span>
        <div className="min-w-0"><div className="font-heading font-bold truncate">{info?.name || "…"}{info?.location ? ` · ${info.location}` : ""}</div><div className="text-xs text-white/60 truncate">{info?.shop}</div></div>
        <span className="ms-auto text-xs flex items-center gap-1" data-testid="phone-cam-status">{running ? (online ? <Wifi className="w-4 h-4 text-[#3DDC84]" /> : <WifiOff className="w-4 h-4 text-[#FF6B6B]" />) : null}{running ? `${sent}` : ""}</span>
      </header>
      <div className="relative flex-1 bg-black"><video ref={videoRef} muted playsInline className="w-full h-full object-contain" />
        {running && <span className="absolute top-3 start-3 px-2 py-0.5 rounded bg-[#C7353B] text-xs font-bold animate-pulse">● {t("cam_live")}</span>}
      </div>
      {err && <div className="m-4 rounded-xl bg-[#C7353B]/20 border border-[#C7353B] p-3 text-sm" data-testid="phone-cam-error">{err}</div>}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex gap-1">{FPS.map((x) => <button key={x} type="button" onClick={() => setFps(x)} className={`px-2.5 py-1.5 rounded-lg ${fps === x ? "bg-white text-black" : "bg-white/10"}`} data-testid={`phone-cam-fps-${x}`}>{x} fps</button>)}</div>
          <div className="flex gap-1">{SIZES.map(([w, l]) => <button key={w} type="button" onClick={() => setSize(w)} className={`px-2.5 py-1.5 rounded-lg ${size === w ? "bg-white text-black" : "bg-white/10"}`} data-testid={`phone-cam-size-${w}`}>{l}</button>)}</div>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <button type="button" disabled={!info} onClick={running ? stop : start} className={`h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 ${running ? "bg-white/15" : "bg-[#3DDC84] text-black"}`} data-testid="phone-cam-toggle"><Zap className="w-5 h-5" />{running ? t("cam_phone_stop") : t("cam_phone_start")}</button>
          <button type="button" onClick={() => setFacing(facing === "environment" ? "user" : "environment")} className="h-14 w-14 rounded-2xl bg-white/10 grid place-items-center" data-testid="phone-cam-flip"><SwitchCamera className="w-6 h-6" /></button>
        </div>
        <p className="text-[11px] text-white/50">{t("cam_phone_tip")}</p>
      </div>
    </div>
  );
}
